import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Tabs,
	Tab,
	Tooltip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { getLastMeetingAlert } from '@/utils/caseAlertHelper';
import LastMeetingInfoHint from '@/common/components/LastMeetingInfoHint';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MeditationListSummaryFilterSection from './components/CaseRelievingSummaryFilterSection';
import { MemberStatusEnum, PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CaseFilter, CasePageDTO } from '@/api/DTO/case-management/caseDto';
import { useGetMyCaseListQuery } from '@/api/case-management/caseApi';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import MemberStatusChip from '@/common/components/MemberStatusChip';

export default function CaseRelievingSummaryPage() {
	const { t } = useTranslation();
	const { userInfo } = useGetUser();
	const canCreateIntervention = userInfo?.permissionList?.includes(
		PermissionEnum.CaseManagementCreateIntervention
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('relieving');

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		if (newValue == 'all') navigate(`/case-management/my-case`);
		if (newValue == 'pending') navigate(`/case-management/my-case/pending`);
		if (newValue == 'relieving') navigate(`/case-management/my-case/relieving`);
		if (newValue == 'shared') navigate(`/case-management/my-case/shared`);
	};

	const defaultPropertyFilter: CaseFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		statusList: [],
		currentRiskLevelList: [],
	};
	const defaultValues: DefaultValues<CaseFilter> = defaultPropertyFilter;
	const methods = useForm<CaseFilter>({
		defaultValues,
	});
	const transformedSearchParams: CaseFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'statusList',
			'currentRiskLevelList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMyCaseListQuery(
			{ ...transformedSearchParams, type: 2 },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: CasePageDTO[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				receivingReferralOrApplicationDate:
					!item.receivingReferralOrApplicationDate
						? '-'
						: dayjs(item.receivingReferralOrApplicationDate)
								.locale('en')
								.format(DISPLAY_DATE_FORMAT),
				lastMeetingInterventionDate: !item.lastMeetingInterventionDate
					? '-'
					: dayjs(item.lastMeetingInterventionDate)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
				dateOfCaseOpen: !item.dateOfCaseOpen
					? '-'
					: dayjs(item.dateOfCaseOpen).locale('en').format(DISPLAY_DATE_FORMAT),
				dueDateOfIntakeCompletion: !item.dueDateOfIntakeCompletion
					? '-'
					: dayjs(item.dueDateOfIntakeCompletion)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
				lastMeetingAlert: getLastMeetingAlert(item),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as CaseFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const createInterventionColumn: GridColDef = {
		field: 'create',
		headerName: '',
		width: 68,
		renderCell: (params) =>
			[
				MemberStatusEnum.InProgressNoAdmitted,
				MemberStatusEnum.InProgressAdmitted,
				MemberStatusEnum.AwaitingAssignmentNoAdmitted,
				MemberStatusEnum.AwaitingAssignmentAdmitted,
			].includes(params.row.status) ? null : (
				<Link
					to={`/case-management/my-case/create/${params.id}/case-relieving`}
				>
					<IconButton color='secondary'>
						<EditCalendarOutlinedIcon />
					</IconButton>
				</Link>
			),
	};

	const columns: GridColDef[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('preAdmission.mobilePhone'),
			flex: 15,
		},
		{
			field: 'dateOfCaseOpen',
			headerName: t('member.dateCaseOpen'),
			flex: 25,
		},
		{
			field: 'currentRiskLevelDesc',
			headerName: t('member.currentRiskLevel'),
			flex: 25,
		},

		{
			field: 'maxRiskLevelDesc',
			headerName: t('case.maxRiskLevel'),
			flex: 15,
		},
		{
			field: 'lastMeetingInterventionDate',
			headerName: t('member.lastMeetingInterventionDate'),
			flex: 15,

			renderHeader: () => (
				<Box sx={{ whiteSpace: 'normal' }}>
					{t('member.lastMeetingInterventionDate')}
					<LastMeetingInfoHint />
				</Box>
			),
			renderCell: (params) =>
				!params.row.lastMeetingAlert ? (
					params.row.lastMeetingInterventionDate
				) : (
					<>
						{params.row.lastMeetingInterventionDate}
						<Tooltip
							title={t(
								params.row.currentRiskLevelDesc
									? 'case.lastMeetingAlertReason'
									: 'case.lastMeetingAlertReasonNoRisk',
								{
									riskLevel: params.row.currentRiskLevelDesc,
									days: params.row.lastMeetingAlert.daysSinceLastMeeting,
									limit: params.row.lastMeetingAlert.alertAfterDays,
								}
							)}
						>
							<ErrorOutlineIcon />
						</Tooltip>
					</>
				),
		},
		{
			field: 'responsibleCaseWorkerName',
			headerName: t('member.responsibleCaseWorker'),
			flex: 15,
		},
		{
			field: 'relievingWorkerName',
			headerName: t('member.relievingWorker'),
			flex: 15,
		},
		{
			field: 'sharedWithProfessionalsDesc',
			headerName: t('member.sharedWithProfessionals'),
			flex: 15,
		},
		{
			field: 'statusDesc',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<a
					href={
						params.row.fileNo
							? `/member-management/member-list/detail/${params.id}`
							: `/member-management/admission-request-list/detail/${params.id}`
					}
					target='_blank'
					rel='noopener noreferrer'
				>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</a>
			),
		},
		...(canCreateIntervention ? [createInterventionColumn] : []),
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.myCaseSummary')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<Tabs
						value={currentTab}
						onChange={handleTabChange}
						sx={{ marginBottom: 3 }}
					>
						<Tab
							label={
								<Typography variant='body1'>{t('case.caseAll')}</Typography>
							}
							value='all'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('case.casePending')}</Typography>
							}
							value='pending'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>
									{t('case.caseRelieving')}
								</Typography>
							}
							value='relieving'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('case.caseShared')}</Typography>
							}
							value='shared'
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
